import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import { EMAIL_VALIDATION_ACTIONS, MAX_NEW_EMAIL_VALIDATION_RULE_LENGTH } from 'components/constants';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import './PopUpCreateEmailValidationRuleScheme.scss';

const PopUpCreateEmailValidationRuleScheme = ({ isLoading, handleClose, handleCreateEmailValidationRule }) => {
  const i18n = useContext(i18nContext);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      rule: '',
      action: ''
    },
    validationSchema: Yup.object().shape({
      rule: Yup.string()
        .required(i18n.getMessage('emailValidationRules.modal.validation.isRequired'))
        .max(
          MAX_NEW_EMAIL_VALIDATION_RULE_LENGTH,
          i18n.getMessage('emailValidationRules.modal.validation.maxSize"', {
            size: MAX_NEW_EMAIL_VALIDATION_RULE_LENGTH
          })
        ),
      action: Yup.string().required(i18n.getMessage('emailValidationRules.modal.validation.isRequired'))
    }),
    onSubmit: async (values) => {
      await handleCreateEmailValidationRule({ rule: values.rule, action: values.action });
    }
  });

  const { values, handleSubmit, errors, handleChange, setFieldValue } = form;

  const emailValidationActionsOptions = Object.keys(EMAIL_VALIDATION_ACTIONS).map((action) => {
    return {
      key: EMAIL_VALIDATION_ACTIONS[action],
      value: i18n.getMessage('emailValidationRules.action.' + EMAIL_VALIDATION_ACTIONS[action])
    };
  });

  return (
    <div className={'scheme-wrapper scheme-create-email-validation-rule-container'}>
      <div className={'scheme-create-email-validation-rule-container-header'}>
        <h4>{i18n.getMessage('emailValidationRules.modal.title')}</h4>
        <CloseIcon
          className={'scheme-create-email-validation-rule-container-header-close-icon'}
          onClick={handleClose}
        />
      </div>
      <form className={'scheme-create-email-validation-rule-form'} onSubmit={handleSubmit}>
        <div className={'scheme-create-email-validation-rule-form-inputs-wrapper'}>
          <Input
            className={'scheme-create-email-validation-rule-input'}
            id={'rule'}
            name={'rule'}
            label={i18n.getMessage('emailValidationRules.modal.input.rule')}
            placeholder={i18n.getMessage('emailValidationRules.modal.placeholder.rule')}
            value={values.rule}
            onChange={handleChange}
            max={MAX_NEW_EMAIL_VALIDATION_RULE_LENGTH}
            error={errors?.rule}
            disabled={isLoading}
          />
        </div>

        <div className={'scheme-create-email-validation-rule-form-inputs-wrapper'}>
          <InputDropDown
            className={'scheme-create-email-validation-rule-input'}
            name={'action'}
            label={i18n.getMessage('emailValidationRules.modal.input.action')}
            placeholder={i18n.getMessage('emailValidationRules.modal.placeholder.action')}
            value={values.action}
            options={emailValidationActionsOptions}
            onChange={(name, value) => setFieldValue(name, value)}
            isDisabled={isLoading}
          />
        </div>

        <div className={'scheme-create-email-validation-rule-actions-wrapper'}>
          <Button
            className={'scheme-create-email-validation-rule-button'}
            type={'outline'}
            onClick={handleClose}
            isDisabled={isLoading}
          >
            {i18n.getMessage('emailValidationRules.modal.button.cancel')}
          </Button>
          <Button
            className={'scheme-create-email-validation-rule-button'}
            roleType={'submit'}
            type={'primary'}
            isDisabled={isLoading}
          >
            {i18n.getMessage('emailValidationRules.modal.button.create')}
          </Button>
        </div>
      </form>
    </div>
  );
};

PopUpCreateEmailValidationRuleScheme.propTypes = {
  isLoading: PropTypes.bool,
  handleClose: PropTypes.func,
  handleCreateEmailValidationRule: PropTypes.func
};

export default PopUpCreateEmailValidationRuleScheme;
